/**
 * Game State Machine for Trickster
 *
 * The game flow is managed by a simple, explicit state machine with the following states:
 *
 *   - 'neutral':    The game is idle. The only action is to start a new game. (Initial state)
 *   - 'selecting':  The user is selecting a song collection to play with.
 *   - 'playing':    The game is in progress. Players take turns placing songs.
 *   - 'finished':   The game is over (all songs placed or someone wins). Only action is to start a new game.
 *
 * State transitions:
 *   neutral   --(Start New Game)-->  selecting
 *   selecting --(Collection chosen & loaded)--> playing
 *   playing   --(Game ends)--> finished
 *   finished  --(New Game)--> neutral
 *
 * Actions:
 *   - RESET_GAME:      Return to 'neutral' state, clear all game data.
 *   - START_NEW_GAME:  Move to 'selecting' state, reset game data.
 *   - SELECT_COLLECTION: Set collection, stay in 'selecting' until songs are loaded.
 *   - SONGS_LOADED:    Move to 'playing', set up songs and players.
 *   - GAME_FINISHED:   Move to 'finished'.
 *   - Other actions (e.g. PLACE_SONG, NEXT_ROUND) are only valid in 'playing'.
 *
 * The UI is a pure function of gameState. Modals are shown in 'neutral', 'selecting', and 'finished'.
 *
 * See README.md and SPEC.md for a full description of the game flow and rationale.
 */

// gameReducer.js
// Reducer and action types for game state management

export const initialGameState = {
  songs: [],
  discardedSongs: [], // Songs that have been played or discarded
  currentSong: null,
  round: 1,
  gameState: 'neutral', // 'neutral', 'selecting', 'playing', 'finished'
  messages: [],
  songRevealed: false,
  yearRevealed: false,
  selectedCollectionFile: null,
  players: [
    { id: 1, name: 'Player 1', timeline: [], score: 0, streak: 0 },
    { id: 2, name: 'Player 2', timeline: [], score: 0, streak: 0 },
  ],
  currentPlayer: 1,
};

export const gameActionTypes = {
  RESET_GAME: 'RESET_GAME',
  START_NEW_GAME: 'START_NEW_GAME',
  SELECT_COLLECTION: 'SELECT_COLLECTION',
  SONGS_LOADED: 'SONGS_LOADED',
  GAME_FINISHED: 'GAME_FINISHED',
  PLACE_SONG: 'PLACE_SONG',
  NEXT_ROUND: 'NEXT_ROUND',
  REVEAL_SONG: 'REVEAL_SONG',
  REVEAL_YEAR: 'REVEAL_YEAR',
  SET_PLAYER_NAME: 'SET_PLAYER_NAME',
  SHOW_MESSAGE: 'SHOW_MESSAGE',
  SET_CURRENT_SONG: 'SET_CURRENT_SONG',
  SET_CURRENT_PLAYER: 'SET_CURRENT_PLAYER',
  REMOVE_EXPIRED_MESSAGES: 'REMOVE_EXPIRED_MESSAGES',
};

export function gameReducer(state, action) {
  switch (action.type) {
  case gameActionTypes.RESET_GAME:
    return {
      ...initialGameState,
      gameState: 'neutral',
      messages: [],
    };
  case gameActionTypes.START_NEW_GAME:
    return {
      ...initialGameState,
      gameState: 'selecting',
      messages: [],
    };
  case gameActionTypes.SELECT_COLLECTION:
    return {
      ...state,
      selectedCollectionFile: action.payload.filePath,
      gameState: 'selecting', // stays in selecting until songs are loaded
    };
  case gameActionTypes.SONGS_LOADED:
    return {
      ...state,
      songs: action.payload.songs,
      discardedSongs: [], // Reset discards on new game
      players: action.payload.players || state.players, // update players with seeded timelines
      gameState: 'playing',
      round: 1,
      currentSong: action.payload.songs[0] || null,
      songRevealed: false,
      yearRevealed: false,
      messages: [],
    };
  case gameActionTypes.PLACE_SONG: {
    // Remove the just-played/discarded song from songs, add to discardedSongs
    const { song } = action.payload;
    let songId = song?.id;
    if (!songId && state.currentSong) songId = state.currentSong.id;
    // Remove _placed_ suffix if present
    songId = songId?.split('_placed_')[0];
    const songs = state.songs.filter((s) => s.id !== songId);
    const discarded = songId ? [...(state.discardedSongs || []), state.songs.find((s) => s.id === songId)].filter(Boolean) : (state.discardedSongs || []);
    return {
      ...state,
      players: action.payload.players,
      songs,
      discardedSongs: discarded,
      currentSong: null,
    };
  }
  case gameActionTypes.NEXT_ROUND:
    return {
      ...state,
      currentSong: action.payload.currentSong,
      songRevealed: false,
      yearRevealed: false,
      round: state.round + 1,
    };
  case gameActionTypes.REVEAL_SONG:
    return { ...state, songRevealed: true };
  case gameActionTypes.REVEAL_YEAR:
    return { ...state, yearRevealed: true };
  case gameActionTypes.SET_PLAYER_NAME:
    return {
      ...state,
      players: state.players.map((player) =>
        player.id === action.payload.playerId
          ? { ...player, name: action.payload.newName }
          : player
      ),
    };
  case gameActionTypes.SHOW_MESSAGE:
    return {
      ...state,
      messages: [...state.messages, action.payload.message],
    };
  case gameActionTypes.GAME_FINISHED:
    return {
      ...state,
      gameState: 'finished',
    };
  case gameActionTypes.SET_CURRENT_SONG:
    return {
      ...state,
      currentSong: action.payload.currentSong,
    };
  case gameActionTypes.SET_CURRENT_PLAYER:
    return {
      ...state,
      currentPlayer: action.payload.playerId,
    };
  case gameActionTypes.REMOVE_EXPIRED_MESSAGES:
    return {
      ...state,
      messages: [],
    };
  default:
    return state;
  }
}
